<script setup lang="ts">
import type { CookieItem } from '~/types/content/cookieItem.type';

const cookies: Record<string, CookieItem[]> = {
    necessary: [
        {
            name: '_GRECAPTCHA',
            provider: 'google.com',
            purpose:
                'Este cookie é usado para distinguir entre humanos e robôs. Isto é útil para o site, a fim de gerar relatórios válidos sobre a utilização do site.',
            expiry: '180 dias',
            type: 'HTTP Cookie',
        },
        {
            name: 'CookieConsent',
            provider: 'consent.cookiebot.com',
            purpose: 'Armazena o estado do consentimento de cookies do utilizador para o domínio atual',
            expiry: '1 ano',
            type: 'HTTP Cookie',
        },
        {
            name: 'iconify-count',
            provider: 'fleequid.com',
            purpose:
                'Utilizado pelo Sistema de Gestão de Conteúdos (CMS) do site para determinar como os menus de separadores do site devem ser apresentados.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
        {
            name: 'iconify-version',
            provider: 'fleequid.com',
            purpose:
                'Utilizado pelo Sistema de Gestão de Conteúdos (CMS) do site para determinar como os menus de separadores do site devem ser apresentados.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::a',
            provider: 'gstatic.com',
            purpose:
                'Este cookie é usado para distinguir entre humanos e robôs. Isto é útil para o site, a fim de gerar relatórios válidos sobre a utilização do site.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::b',
            provider: 'gstatic.com',
            purpose: 'Este cookie é usado para distinguir entre humanos e robôs.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::c',
            provider: 'gstatic.com',
            purpose: 'Este cookie é usado para distinguir entre humanos e robôs.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::f',
            provider: 'gstatic.com',
            purpose: 'Este cookie é usado para distinguir entre humanos e robôs.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
    ],
    preferences: [
        {
            name: 'i18n_redirected',
            provider: 'fleequid.com',
            purpose: 'Determina o idioma preferido do visitante. Permite ao site definir o idioma preferido quando o visitante regressa.',
            expiry: '1 ano',
            type: 'HTTP Cookie',
        },
    ],
    marketing: [
        {
            name: '_ga',
            provider: 'www.googletagmanager.com',
            purpose:
                'Utilizado para enviar dados ao Google Analytics sobre o dispositivo e o comportamento do utilizador. Rastreia o utilizador através de dispositivos e canais de marketing.',
            expiry: '2 anos',
            type: 'HTTP Cookie',
        },
        {
            name: '_ga#',
            provider: 'www.googletagmanager.com',
            purpose:
                'Utilizado para enviar dados ao Google Analytics sobre o dispositivo e o comportamento do utilizador. Rastreia o utilizador através de dispositivos e canais de marketing.',
            expiry: '2 anos',
            type: 'HTTP Cookie',
        },
    ],
    unclassified: [
        {
            name: 'nuxt-color-mode',
            provider: 'fleequid.com',
            purpose: 'Determina o tema preferido do visitante.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
    ],
};
</script>
<template>
    <ContentPage title="Política de Cookies">
        <p>
            Utilizamos cookies para personalizar conteúdos e anúncios, para fornecer funcionalidades de redes sociais e para analisar o nosso tráfego.
            Também partilhamos informações sobre como utiliza o nosso site com os nossos parceiros de análise web, publicidade e redes sociais, que
            podem combiná-las com outras informações.
        </p>
        <p>Os cookies são pequenos ficheiros de texto que podem ser utilizados pelos sites para tornar a experiência do utilizador mais eficiente.</p>
        <p>
            A lei estabelece que podemos armazenar cookies no seu dispositivo se forem estritamente necessários para o funcionamento deste site. Para
            todos os outros tipos de cookies, precisamos do seu consentimento.
        </p>
        <p>Este site utiliza diferentes tipos de cookies. Alguns cookies são colocados por serviços de terceiros que aparecem nas nossas páginas.</p>
        <p>Pode alterar ou retirar o seu consentimento a qualquer momento através da Declaração de Cookies no nosso site.</p>
        <p>
            Saiba mais sobre quem somos, como pode contactar-nos e como tratamos os dados pessoais na nossa
            <NuxtLinkLocale to="privacy-policy" class="text-primary-500 hover:underline">Política de Privacidade</NuxtLinkLocale>.
        </p>
        <p>Especifique o seu ID de consentimento e a data em que nos contactou relativamente ao seu consentimento.</p>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Necessários (8)</h3>
            <p>
                Os cookies necessários ajudam a tornar um site utilizável, permitindo funções básicas como navegação nas páginas e acesso a áreas
                seguras do site. O site não pode funcionar corretamente sem estes cookies.
            </p>
            <ContentCookieTable :items="cookies.necessary" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Preferências (1)</h3>
            <p>
                Os cookies de preferências permitem ao site memorizar informações que alteram o comportamento ou a aparência do site, como o seu
                idioma preferido ou a região onde se encontra.
            </p>
            <ContentCookieTable :items="cookies.preferences" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Marketing (2)</h3>
            <p>
                Os cookies de marketing são utilizados para rastrear visitantes em sites. A intenção é exibir anúncios que sejam relevantes e
                atraentes para o utilizador individual e, portanto, mais valiosos para editores e anunciantes terceiros.
            </p>
            <ContentCookieTable :items="cookies.marketing" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Não classificados (1)</h3>
            <p>
                Os cookies não classificados são cookies que estão em processo de classificação, juntamente com os fornecedores de cookies
                individuais.
            </p>
            <ContentCookieTable :items="cookies.unclassified" />
        </div>
    </ContentPage>
</template>
